'use client';

import { useState, useEffect, useCallback, useRef, useMemo, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import api from '@/lib/api';
import { aiModelsApi } from '@/lib/ai-models-api';
import { JobResponse, HistoryItem, QueueStats, HealthResponse, JobCreate, BatchJobCreate, Series, PermutationList, PermutationListItem } from '@/lib/types';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmDialog';
import GenerationForm from '@/components/GenerationForm';
import JobCard from '@/components/JobCard';
import HistoryPanel from '@/components/HistoryPanel';
import StatusBar from '@/components/StatusBar';
import ImageModal from '@/components/ImageModal';
import SeriesPanel from '@/components/SeriesPanel';

// Filter out comment lines (starting with --) from prompt, matching backend behavior
function filterPromptComments(prompt: string): string {
  if (!prompt) return prompt;
  return prompt
    .split('\n')
    .filter(line => !line.trimStart().startsWith('--'))
    .join('\n');
}

// Debounce helper
function debounce<T extends (...args: Parameters<T>) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

interface CollageListCardProps {
  list: PermutationList;
  matchedItemValue: string | null;
  selectedIndex: number;
  isFocused: boolean;
  isCollapsed: boolean;
  onSelect: (index: number) => void;
  onToggleCollapse: () => void;
  onRemove: () => void;
}

function CollageListCard({
  list,
  matchedItemValue,
  selectedIndex,
  isFocused,
  isCollapsed,
  onSelect,
  onToggleCollapse,
  onRemove,
}: CollageListCardProps) {
  const activeItems = list.items.filter(i => i.is_active);

  // Get 5 items: -2, -1, current, +1, +2
  const visibleIndices = [
    selectedIndex - 2,
    selectedIndex - 1,
    selectedIndex,
    selectedIndex + 1,
    selectedIndex + 2,
  ];

  const renderThumbnail = (index: number, position: 'far' | 'near' | 'center') => {
    const item = activeItems[index];
    const isValid = index >= 0 && index < activeItems.length;

    // Size based on position
    const sizeClass = position === 'center' ? 'w-14 h-14' : position === 'near' ? 'w-11 h-11' : 'w-9 h-9';
    const opacityClass = position === 'center' ? 'opacity-100' : position === 'near' ? 'opacity-70' : 'opacity-40';
    const ringClass = position === 'center' ? 'ring-2 ring-[var(--accent)]' : '';

    if (!isValid || !item) {
      return (
        <div
          className={`${sizeClass} rounded-lg bg-[var(--bg-secondary)] flex-shrink-0`}
        />
      );
    }

    const content = item.preview_image ? (
      <img
        src={api.getImageUrl(item.preview_image)}
        alt={item.value}
        className={`${sizeClass} rounded-lg object-cover ${opacityClass} ${ringClass} flex-shrink-0`}
      />
    ) : (
      (() => {
        const letter = item.value.charAt(0).toUpperCase();
        const colors = ['bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-500', 'bg-teal-500'];
        const colorIndex = item.value.length % colors.length;
        return (
          <div
            className={`${sizeClass} rounded-lg ${colors[colorIndex]} flex items-center justify-center text-white font-bold text-xs ${opacityClass} ${ringClass} flex-shrink-0`}
          >
            {position !== 'far' ? letter : ''}
          </div>
        );
      })()
    );

    return (
      <button
        onClick={() => onSelect(index)}
        className="cursor-pointer hover:scale-105 transition-transform focus:outline-none"
        title={item.value}
      >
        {content}
      </button>
    );
  };

  return (
    <div
      className={`rounded-xl border transition-all ${
        isFocused
          ? 'border-[var(--accent)] bg-[var(--accent-soft)]'
          : matchedItemValue
            ? 'border-green-500/50 bg-green-500/5'
            : 'border-[var(--border-subtle)] bg-[var(--bg-elevated)]'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-subtle)]">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-medium truncate">{list.name}</span>
          {matchedItemValue && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 truncate max-w-[80px]">
              matched
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            onClick={onToggleCollapse}
            className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            <svg className={`w-3 h-3 transition-transform ${isCollapsed ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            onClick={onRemove}
            className="p-1 text-[var(--text-muted)] hover:text-[var(--error)] transition-colors"
            title="Remove list"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="p-2 space-y-1.5">
          {/* 5-item carousel */}
          <div className="flex items-center justify-center gap-1">
            <button
              onClick={() => onSelect(Math.max(0, selectedIndex - 1))}
              disabled={selectedIndex === 0}
              className="p-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-30 transition-colors flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex items-center justify-center gap-1">
              {renderThumbnail(visibleIndices[0], 'far')}
              {renderThumbnail(visibleIndices[1], 'near')}
              {renderThumbnail(visibleIndices[2], 'center')}
              {renderThumbnail(visibleIndices[3], 'near')}
              {renderThumbnail(visibleIndices[4], 'far')}
            </div>

            <button
              onClick={() => onSelect(Math.min(activeItems.length - 1, selectedIndex + 1))}
              disabled={selectedIndex >= activeItems.length - 1}
              className="p-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-30 transition-colors flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Current item value */}
          <p className="text-center text-[10px] text-[var(--text-muted)] truncate px-1">
            {activeItems[selectedIndex]?.value || 'No items'}
          </p>

          {/* Position indicator */}
          <div className="text-center text-[10px] text-[var(--text-muted)]">
            {selectedIndex + 1} / {activeItems.length}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CollagePage() {
  return (
    <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
      <CollageContent />
    </Suspense>
  );
}

function CollageContent() {
  const searchParams = useSearchParams();

  // Data state (same as Create page)
  const [jobs, setJobs] = useState<JobResponse[]>([]);
  const jobsRef = useRef<JobResponse[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState<Partial<JobCreate>>({});
  const [series, setSeries] = useState<Series[]>([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState<number | null>(null);
  const [urlParamsApplied, setUrlParamsApplied] = useState(false);
  const toast = useToast();
  const { confirm } = useConfirm();

  // Collage-specific state
  const [lists, setLists] = useState<PermutationList[]>([]);
  const [activeLists, setActiveLists] = useState<number[]>([]); // List IDs in display order
  const [selectedItems, setSelectedItems] = useState<Map<number, number>>(new Map()); // listId → itemIndex
  const [focusedListIndex, setFocusedListIndex] = useState(0);
  const [collapsedLists, setCollapsedLists] = useState<Set<number>>(new Set());

  // Controlled prompt state for CollageNavigator integration
  const [prompt, setPrompt] = useState('');

  // Dimensions and seed from GenerationForm (for preview)
  const [dimensions, setDimensions] = useState({ width: 1024, height: 1024 });
  const [previewSeed, setPreviewSeed] = useState<number | null>(null);

  // Memoized callbacks to prevent infinite loop
  const handleDimensionsChange = useCallback((w: number, h: number) => {
    setDimensions(prev => {
      if (prev.width === w && prev.height === h) return prev;
      return { width: w, height: h };
    });
  }, []);

  const handleSeedChange = useCallback((seed: number | null) => {
    setPreviewSeed(prev => prev === seed ? prev : seed);
  }, []);

  // Preview state - single image reused across navigator, floating window, and modal
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewEnabled, setPreviewEnabled] = useState(true);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewDimensions, setPreviewDimensions] = useState({ width: 512, height: 512 });

  // Floating preview state
  const [floatingPreviewOpen, setFloatingPreviewOpen] = useState(false);
  const [floatingPos, setFloatingPos] = useState({ x: -1, y: 80 }); // -1 signals "use default"
  const [floatingWidth, setFloatingWidth] = useState(512);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const resizeStartRef = useRef({ mouseX: 0, mouseY: 0, width: 512 });

  // Throttle refs for preview generation
  const lastGenerationTimeRef = useRef(0);
  const pendingGenerationRef = useRef<{ prompt: string; width: number; height: number; seed: number | null } | null>(null);
  const throttleTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize floating position on mount
  useEffect(() => {
    if (floatingPos.x === -1 && typeof window !== 'undefined') {
      setFloatingPos({ x: window.innerWidth - floatingWidth - 36, y: 80 });
    }
  }, [floatingPos.x, floatingWidth]);

  // Drag handling for floating window
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      setFloatingPos({
        x: e.clientX - dragOffsetRef.current.x,
        y: e.clientY - dragOffsetRef.current.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Resize handling for floating window
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizeStartRef.current.mouseX;
      const newWidth = Math.max(200, Math.min(resizeStartRef.current.width + delta, previewDimensions.width));
      setFloatingWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, previewDimensions.width]);

  // Auto-detect lists from prompt text and update activeLists
  useEffect(() => {
    if (!prompt || lists.length === 0) return;

    // Find all lists that have a matching item in the prompt
    const detectedListIds: number[] = [];
    const detectedMatches = new Map<number, { value: string; index: number }>();

    for (const list of lists) {
      const activeItems = list.items.filter(i => i.is_active);
      for (let i = 0; i < activeItems.length; i++) {
        const item = activeItems[i];
        if (item.value && prompt.includes(item.value)) {
          detectedListIds.push(list.id);
          detectedMatches.set(list.id, { value: item.value, index: i });
          break; // Only first match per list
        }
      }
    }

    // Update activeLists: keep existing order, add new detected lists at the end
    setActiveLists(prev => {
      const existingWithMatches = prev.filter(id => detectedListIds.includes(id));
      const newLists = detectedListIds.filter(id => !prev.includes(id));

      // Keep existing lists that still have matches, add new ones
      if (newLists.length > 0 || existingWithMatches.length !== prev.filter(id => detectedListIds.includes(id)).length) {
        return [...existingWithMatches, ...newLists];
      }
      return prev;
    });

    // Update selectedItems for detected matches
    setSelectedItems(prev => {
      const newSelected = new Map(prev);
      let changed = false;

      for (const [listId, match] of detectedMatches) {
        if (newSelected.get(listId) !== match.index) {
          newSelected.set(listId, match.index);
          changed = true;
        }
      }

      return changed ? newSelected : prev;
    });
  }, [prompt, lists]);

  // Find which list items are matched in the current prompt (for display)
  const matchedItems = useMemo(() => {
    const matches = new Map<number, string>(); // listId → matched value
    if (!prompt) return matches;

    for (const listId of activeLists) {
      const list = lists.find(l => l.id === listId);
      if (!list) continue;

      const activeItems = list.items.filter(i => i.is_active);
      for (const item of activeItems) {
        if (item.value && prompt.includes(item.value)) {
          matches.set(listId, item.value);
          break; // Only first match per list
        }
      }
    }
    return matches;
  }, [prompt, activeLists, lists]);

  // Handle item selection in CollageNavigator - replace text in prompt
  const handleItemSelect = useCallback((listId: number, newIndex: number) => {
    const list = lists.find(l => l.id === listId);
    if (!list) return;

    const activeItems = list.items.filter(i => i.is_active);
    const oldIndex = selectedItems.get(listId) ?? 0;
    const oldItem = activeItems[oldIndex];
    const newItem = activeItems[newIndex];

    if (!newItem) return;

    // Update selected items
    setSelectedItems(prev => new Map(prev).set(listId, newIndex));

    // Replace text in prompt
    if (oldItem && prompt.includes(oldItem.value)) {
      setPrompt(prompt.replace(oldItem.value, newItem.value));
    } else if (!prompt.includes(newItem.value)) {
      // If old item not in prompt, append new item on new line
      setPrompt(prev => prev ? `${prev}\n${newItem.value}` : newItem.value);
    }
  }, [lists, selectedItems, prompt]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [healthRes, statsRes, jobsRes, historyRes, seriesRes, listsRes] = await Promise.all([
        api.getHealth(),
        api.getQueueStats(),
        api.listJobs(undefined, 500, 0, selectedSeriesId ?? undefined),
        api.getHistory(50),
        api.getSeries(),
        api.getPermutationLists(),
      ]);
      setHealth(healthRes);
      setStats(statsRes);
      setJobs(jobsRes);
      jobsRef.current = jobsRes;
      setHistory(historyRes);
      setSeries(seriesRes);
      setLists(listsRes);

      // Auto-select first 3 lists if none selected yet
      if (activeLists.length === 0 && listsRes.length > 0) {
        const defaultLists = listsRes
          .filter(l => l.items.some(i => i.is_active))
          .slice(0, 3)
          .map(l => l.id);
        setActiveLists(defaultLists);

        // Initialize selected items to first item in each list
        const initialSelected = new Map<number, number>();
        defaultLists.forEach(listId => initialSelected.set(listId, 0));
        setSelectedItems(initialSelected);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [selectedSeriesId, activeLists.length]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      const hasPendingOrProcessing = jobsRef.current.some(
        j => j.status === 'pending' || j.status === 'processing'
      );
      if (hasPendingOrProcessing) {
        fetchData();
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Apply URL parameters on mount (for "Use in Collage" from variants page)
  useEffect(() => {
    if (urlParamsApplied) return;

    const promptParam = searchParams.get('prompt');
    const seriesIdParam = searchParams.get('series_id');
    const widthParam = searchParams.get('width');
    const heightParam = searchParams.get('height');
    const stepsParam = searchParams.get('steps');
    const cfgParam = searchParams.get('cfg');
    const seedParam = searchParams.get('seed');

    if (promptParam) {
      setPrompt(decodeURIComponent(promptParam));

      const newFormValues: Partial<JobCreate> = {
        prompt: decodeURIComponent(promptParam),
      };

      if (widthParam) newFormValues.width = parseInt(widthParam);
      if (heightParam) newFormValues.height = parseInt(heightParam);
      if (stepsParam) newFormValues.steps = parseInt(stepsParam);
      if (cfgParam) newFormValues.cfg = parseFloat(cfgParam);
      if (seedParam && seedParam !== 'null') newFormValues.seed = parseInt(seedParam);
      if (seriesIdParam) newFormValues.series_id = parseInt(seriesIdParam);

      setFormValues(newFormValues);

      if (seriesIdParam) {
        setSelectedSeriesId(parseInt(seriesIdParam));
      }

      setUrlParamsApplied(true);
      toast.success('Prompt loaded from Variants page');
    }
  }, [searchParams, urlParamsApplied, toast]);

  // Debounced preview generation with correct aspect ratio and seed
  const generatePreview = useCallback(async (promptText: string, width: number, height: number, seed: number | null) => {
    if (!promptText.trim() || !previewEnabled) return;

    setPreviewLoading(true);

    try {
      const baseSize = 512;
      const aspectRatio = width / height;
      let previewWidth: number;
      let previewHeight: number;

      // Round to nearest multiple of 16
      const roundTo16 = (n: number) => Math.round(n / 16) * 16;

      if (aspectRatio >= 1) {
        // Landscape or square: height is the smaller dimension
        previewHeight = baseSize;
        previewWidth = Math.max(baseSize, roundTo16(baseSize * aspectRatio));
      } else {
        // Portrait: width is the smaller dimension
        previewWidth = baseSize;
        previewHeight = Math.max(baseSize, roundTo16(baseSize / aspectRatio));
      }

      const result = await aiModelsApi.imagegenGenerate({
        prompt: filterPromptComments(promptText),
        width: previewWidth,
        height: previewHeight,
        num_inference_steps: 4,
        seed: seed,
        model: 'z-image-turbo',
      });

      setPreviewImage(result.image);
      setPreviewDimensions({ width: previewWidth, height: previewHeight });
      lastGenerationTimeRef.current = Date.now();
    } catch (error) {
      console.error('Preview generation failed:', error);
    } finally {
      setPreviewLoading(false);
    }
  }, [previewEnabled]);

  // Throttled generation: enforces minimum 2s gap when floating preview is open
  const throttledGenerate = useCallback((promptText: string, width: number, height: number, seed: number | null) => {
    if (!floatingPreviewOpen) {
      // No throttle when floating preview is closed
      generatePreview(promptText, width, height, seed);
      return;
    }

    const now = Date.now();
    const elapsed = now - lastGenerationTimeRef.current;
    const minGap = 2000;

    if (elapsed >= minGap) {
      // Enough time passed, generate immediately
      generatePreview(promptText, width, height, seed);
    } else {
      // Queue the latest params and schedule generation after cooldown
      pendingGenerationRef.current = { prompt: promptText, width, height, seed };
      if (!throttleTimerRef.current) {
        throttleTimerRef.current = setTimeout(() => {
          throttleTimerRef.current = null;
          const pending = pendingGenerationRef.current;
          if (pending) {
            pendingGenerationRef.current = null;
            generatePreview(pending.prompt, pending.width, pending.height, pending.seed);
          }
        }, minGap - elapsed);
      }
    }
  }, [floatingPreviewOpen, generatePreview]);

  // Clean up throttle timer
  useEffect(() => {
    return () => {
      if (throttleTimerRef.current) clearTimeout(throttleTimerRef.current);
    };
  }, []);

  const debouncedGeneratePreview = useMemo(
    () => debounce((promptText: string, width: number, height: number, seed: number | null) => throttledGenerate(promptText, width, height, seed), 800),
    [throttledGenerate]
  );

  useEffect(() => {
    if (prompt.trim() && previewEnabled) {
      debouncedGeneratePreview(prompt, dimensions.width, dimensions.height, previewSeed);
    }
  }, [prompt, dimensions, previewSeed, previewEnabled, debouncedGeneratePreview]);

  // Keyboard navigation for CollageNavigator
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if not in an input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      // Don't handle when image modal is open (it has its own keyboard handler)
      if (selectedJob) return;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setFocusedListIndex(prev => Math.max(0, prev - 1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedListIndex(prev => Math.min(activeLists.length - 1, prev + 1));
          break;
        case 'ArrowLeft':
          e.preventDefault();
          {
            const focusedListId = activeLists[focusedListIndex];
            if (focusedListId !== undefined) {
              const currentIndex = selectedItems.get(focusedListId) ?? 0;
              handleItemSelect(focusedListId, Math.max(0, currentIndex - 1));
            }
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          {
            const focusedListId = activeLists[focusedListIndex];
            if (focusedListId !== undefined) {
              const list = lists.find(l => l.id === focusedListId);
              const activeItems = list?.items.filter(i => i.is_active) ?? [];
              const currentIndex = selectedItems.get(focusedListId) ?? 0;
              handleItemSelect(focusedListId, Math.min(activeItems.length - 1, currentIndex + 1));
            }
          }
          break;
        case ' ':
          e.preventDefault();
          {
            const focusedListId = activeLists[focusedListIndex];
            if (focusedListId !== undefined) {
              setCollapsedLists(prev => {
                const next = new Set(prev);
                if (next.has(focusedListId)) {
                  next.delete(focusedListId);
                } else {
                  next.add(focusedListId);
                }
                return next;
              });
            }
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeLists, focusedListIndex, lists, selectedItems, handleItemSelect, selectedJob]);

  // Handlers (same as Create page)
  const handleSubmit = async (jobData: JobCreate) => {
    setIsSubmitting(true);
    try {
      await api.createJob(jobData);
      await fetchData();
    } catch (error) {
      console.error('Failed to create job:', error);
      toast.error('Failed to create job: ' + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBatchSubmit = async (request: BatchJobCreate) => {
    const preview = await api.previewPermutations(request.prompt);
    if (preview.total_permutations > 20) {
      const confirmed = await confirm({
        title: 'Large Batch',
        message: `This will create ${preview.total_permutations} jobs. Continue?`,
        confirmText: 'Generate All',
        variant: 'warning',
      });
      if (!confirmed) return;
    }

    setIsSubmitting(true);
    try {
      const result = await api.generatePermutations(request);
      await fetchData();
      toast.success(`Created ${result.total_jobs} jobs from permutations`);
    } catch (error) {
      console.error('Failed to create batch jobs:', error);
      toast.error('Failed to create batch jobs: ' + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async (jobId: string, force: boolean = false) => {
    try {
      await api.cancelJob(jobId, force);
      await fetchData();
    } catch (error) {
      console.error('Failed to cancel job:', error);
      toast.error('Failed to cancel job: ' + (error as Error).message);
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    // Optimistically remove from local state immediately
    setJobs(prev => prev.filter(j => j.job_id !== jobId));
    try {
      await api.deleteJobPermanent(jobId);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete job:', error);
      await fetchData(); // Restore correct state on failure
    }
  };

  const handleDeleteHistoryItem = async (itemId: number) => {
    try {
      await api.deleteHistoryItem(itemId);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete history item:', error);
    }
  };

  const handleReuseFromHistory = (item: HistoryItem) => {
    setPrompt(item.prompt);
    setFormValues({
      prompt: item.prompt,
      width: item.params.width,
      height: item.params.height,
      steps: item.params.steps,
      cfg: item.params.cfg,
      seed: item.params.seed,
      series_id: item.params.series_id ?? undefined,
    });
    if (item.job_type === 'batch' && item.params.series_id) {
      setSelectedSeriesId(item.params.series_id);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReuseFromJob = (job: JobResponse) => {
    setPrompt(job.prompt);
    setFormValues({
      prompt: job.prompt,
      width: job.width,
      height: job.height,
      steps: job.steps,
      cfg: job.cfg,
      seed: job.seed,
    });
    setSelectedJob(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearHistory = async (period?: 'today' | 'yesterday' | 'all') => {
    const periodLabels = { today: "today's", yesterday: "yesterday's", all: 'all' };
    const label = periodLabels[period || 'all'];

    const confirmed = await confirm({
      title: 'Clear History',
      message: `This will permanently delete ${label} history items.`,
      confirmText: period === 'all' ? 'Clear All' : `Clear ${label.charAt(0).toUpperCase() + label.slice(1)}`,
      variant: 'danger',
    });

    if (confirmed) {
      try {
        const result = await api.clearHistoryByPeriod(period || 'all');
        await fetchData();
        toast.success(`Cleared ${result.cleared} history items`);
      } catch (error) {
        console.error('Failed to clear history:', error);
        toast.error('Failed to clear history');
      }
    }
  };

  const handleAddList = (listId: number) => {
    if (!activeLists.includes(listId)) {
      setActiveLists(prev => [...prev, listId]);
      setSelectedItems(prev => new Map(prev).set(listId, 0));
    }
  };

  const handleRemoveList = (listId: number) => {
    setActiveLists(prev => prev.filter(id => id !== listId));
    setSelectedItems(prev => {
      const next = new Map(prev);
      next.delete(listId);
      return next;
    });
    setFocusedListIndex(prev => Math.min(prev, activeLists.length - 2));
  };

  const activeJobs = jobs.filter(j => j.status === 'pending' || j.status === 'processing');
  const completedJobs = jobs.filter(j => j.status === 'completed');
  const availableLists = lists.filter(l => !activeLists.includes(l.id) && l.items.some(i => i.is_active));

  const totalImages = series.reduce((sum, s) => sum + s.image_count, 0) +
    jobs.filter(j => j.status === 'completed' && !j.series_id).length;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <StatusBar health={health} stats={stats} onRefresh={fetchData} />

      <div className="flex-1 flex overflow-hidden">
        {/* Series Sidebar (same as Create page) */}
        <aside className="hidden lg:block w-64 border-r border-[var(--border-subtle)] flex-shrink-0 overflow-y-auto">
          <SeriesPanel
            series={series}
            selectedSeriesId={selectedSeriesId}
            totalImages={totalImages}
            onSelectSeries={setSelectedSeriesId}
            onSeriesChange={fetchData}
          />
        </aside>

        {/* Collage Navigator Sidebar */}
        <aside className="hidden lg:flex lg:flex-col w-72 border-r border-[var(--border-subtle)] flex-shrink-0 bg-[var(--bg-secondary)]">
          {/* Fixed top section: Header + Preview */}
          <div className="flex-shrink-0 p-3 space-y-3 border-b border-[var(--border-subtle)]">
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                Navigator
              </h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setFloatingPreviewOpen(prev => !prev)}
                  className={`p-1 rounded transition-colors ${floatingPreviewOpen ? 'text-[var(--accent)] bg-[var(--accent-soft)]' : 'text-[var(--text-muted)]'}`}
                  title={floatingPreviewOpen ? 'Close floating preview' : 'Open floating preview'}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <rect x="2" y="3" width="20" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                    <rect x="12" y="10" width="10" height="10" rx="1" fill="currentColor" opacity={floatingPreviewOpen ? 0.3 : 0} strokeLinecap="round" strokeLinejoin="round" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 10h10v10H12z" />
                  </svg>
                </button>
                <button
                  onClick={() => setPreviewEnabled(!previewEnabled)}
                  className={`p-1 rounded transition-colors ${previewEnabled ? 'text-[var(--accent)] bg-[var(--accent-soft)]' : 'text-[var(--text-muted)]'}`}
                  title={previewEnabled ? 'Disable preview' : 'Enable preview'}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Preview thumbnail */}
            {previewEnabled && (
              <div
                className="relative rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] overflow-hidden"
                style={{ aspectRatio: `${dimensions.width} / ${dimensions.height}` }}
              >
                {previewLoading ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-5 h-5 animate-spin text-[var(--accent)]" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  </div>
                ) : previewImage ? (
                  <img
                    src={previewImage.startsWith('data:') ? previewImage : `data:image/png;base64,${previewImage}`}
                    alt="Preview"
                    className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setPreviewModalOpen(true)}
                    title="Click to enlarge"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-[var(--text-muted)]">
                    <svg className="w-8 h-8 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Scrollable section: Lists + Add + Keyboard hints */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {/* Active Lists */}
            <div className="space-y-2">
              {activeLists.map((listId, index) => {
                const list = lists.find(l => l.id === listId);
                if (!list) return null;
                return (
                  <CollageListCard
                    key={listId}
                    list={list}
                    matchedItemValue={matchedItems.get(listId) || null}
                    selectedIndex={selectedItems.get(listId) ?? 0}
                    isFocused={index === focusedListIndex}
                    isCollapsed={collapsedLists.has(listId)}
                    onSelect={(idx) => handleItemSelect(listId, idx)}
                    onToggleCollapse={() => {
                      setCollapsedLists(prev => {
                        const next = new Set(prev);
                        if (next.has(listId)) next.delete(listId);
                        else next.add(listId);
                        return next;
                      });
                    }}
                    onRemove={() => handleRemoveList(listId)}
                  />
                );
              })}
            </div>

            {/* Add List */}
            {availableLists.length > 0 && (
              <select
                className="w-full px-2 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-xs focus:outline-none focus:ring-1 focus:ring-[var(--accent)]"
                value=""
                onChange={(e) => handleAddList(parseInt(e.target.value))}
              >
                <option value="">+ Add List...</option>
                {availableLists.map(list => (
                  <option key={list.id} value={list.id}>
                    {list.name} ({list.active_count})
                  </option>
                ))}
              </select>
            )}

            {/* Keyboard hints */}
            <div className="pt-2 border-t border-[var(--border-subtle)]">
              <p className="text-[10px] text-[var(--text-muted)] mb-1">Keyboard</p>
              <div className="grid grid-cols-2 gap-0.5 text-[10px] text-[var(--text-muted)]">
                <span className="font-mono bg-[var(--bg-primary)] px-1 rounded">↑↓</span>
                <span>List</span>
                <span className="font-mono bg-[var(--bg-primary)] px-1 rounded">←→</span>
                <span>Item</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content (same as Create page) */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-6 py-8 space-y-10">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <h1 className="text-3xl font-semibold tracking-tight">Collage</h1>
                <p className="text-[var(--text-muted)]">Navigate permutation lists and generate combinations</p>
              </div>
              <button
                type="submit"
                form="collage-gen-form"
                disabled={isSubmitting || !prompt.trim()}
                className="btn btn-primary h-10 px-5 font-semibold shrink-0"
              >
                Generate
              </button>
            </div>

            {/* Generation Form (with controlled prompt) */}
            <GenerationForm
              formId="collage-gen-form"
              key={JSON.stringify(formValues)}
              onSubmit={handleSubmit}
              onBatchSubmit={handleBatchSubmit}
              isLoading={isSubmitting}
              initialValues={formValues}
              series={series}
              selectedSeriesId={selectedSeriesId}
              promptValue={prompt}
              onPromptChange={setPrompt}
              onDimensionsChange={handleDimensionsChange}
              onSeedChange={handleSeedChange}
            />

            {/* Active Jobs */}
            {activeJobs.length > 0 && (
              <section className="space-y-4">
                <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  In Progress
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {activeJobs.map((job) => (
                    <JobCard
                      key={job.job_id}
                      job={job}
                      onCancel={handleCancel}
                      onSelect={setSelectedJob}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Gallery */}
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  Gallery
                </h2>
                {completedJobs.length > 0 && (
                  <span className="text-xs text-[var(--text-muted)]">
                    {completedJobs.length} images
                  </span>
                )}
              </div>

              {completedJobs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 rounded-full bg-[var(--bg-elevated)] flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                    </svg>
                  </div>
                  <p className="text-[var(--text-muted)]">No images yet</p>
                  <p className="text-sm text-[var(--text-muted)] mt-1">Your creations will appear here</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {completedJobs.map((job) => (
                    <JobCard
                      key={job.job_id}
                      job={job}
                      onSelect={setSelectedJob}
                      onDelete={handleDeleteJob}
                      onCopy={handleReuseFromJob}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        </main>

        {/* History Sidebar (same as Create page) */}
        <aside className="hidden lg:block w-72 border-l border-[var(--border-subtle)] flex-shrink-0 overflow-hidden">
          <HistoryPanel
            history={history}
            onReuseParams={handleReuseFromHistory}
            onDeleteItem={handleDeleteHistoryItem}
            onClearHistory={handleClearHistory}
          />
        </aside>
      </div>

      {/* Floating Preview Window */}
      {floatingPreviewOpen && previewEnabled && (
        <div
          className="fixed z-40 shadow-2xl rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)] select-none"
          style={{
            left: floatingPos.x,
            top: floatingPos.y,
            width: floatingWidth,
          }}
        >
          {/* Title bar - drag handle */}
          <div
            className="flex items-center justify-between px-3 py-1.5 bg-[var(--bg-secondary)] border-b border-[var(--border-subtle)] cursor-move select-none"
            onMouseDown={(e) => {
              dragOffsetRef.current = {
                x: e.clientX - floatingPos.x,
                y: e.clientY - floatingPos.y,
              };
              setIsDragging(true);
              e.preventDefault();
            }}
          >
            <span className="text-xs font-medium text-[var(--text-muted)]">Preview</span>
            <button
              onClick={() => setFloatingPreviewOpen(false)}
              className="p-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              title="Close floating preview"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Preview content */}
          <div
            className="relative bg-[var(--bg-primary)]"
            style={{ aspectRatio: `${dimensions.width} / ${dimensions.height}` }}
          >
            {previewImage ? (
              <img
                src={previewImage.startsWith('data:') ? previewImage : `data:image/png;base64,${previewImage}`}
                alt="Preview"
                className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setPreviewModalOpen(true)}
                title="Click to enlarge"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-[var(--text-muted)]">
                <svg className="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
            {/* Loading spinner overlay */}
            {previewLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <svg className="w-6 h-6 animate-spin text-[var(--accent)]" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            )}
          </div>
          {/* Resize handle - bottom right corner */}
          <div
            className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize flex items-end justify-end p-0.5"
            onMouseDown={(e) => {
              resizeStartRef.current = {
                mouseX: e.clientX,
                mouseY: e.clientY,
                width: floatingWidth,
              };
              setIsResizing(true);
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <svg className="w-3 h-3 text-[var(--text-muted)] opacity-40" viewBox="0 0 12 12" fill="currentColor">
              <circle cx="10" cy="10" r="1.5" />
              <circle cx="6" cy="10" r="1.5" />
              <circle cx="10" cy="6" r="1.5" />
              <circle cx="2" cy="10" r="1.5" />
              <circle cx="6" cy="6" r="1.5" />
              <circle cx="10" cy="2" r="1.5" />
            </svg>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewModalOpen && previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm cursor-pointer"
          onClick={() => setPreviewModalOpen(false)}
        >
          <div className="relative">
            <img
              src={previewImage.startsWith('data:') ? previewImage : `data:image/png;base64,${previewImage}`}
              alt="Preview"
              style={{
                width: previewDimensions.width,
                height: previewDimensions.height,
              }}
              className="rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedJob && selectedJob.status === 'completed' && (
        <ImageModal
          job={selectedJob}
          jobs={completedJobs}
          onClose={() => setSelectedJob(null)}
          onReuseParams={handleReuseFromJob}
          onDelete={handleDeleteJob}
          onNavigate={setSelectedJob}
          series={series}
          onSeriesChange={fetchData}
        />
      )}
    </div>
  );
}
